import { useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { displayName, domainOf, formatMailbox, isValidEmail, parseAddressList, parseMailbox, type MessagePage, type Mailbox } from '@dts/core';
import { useContacts } from '@/lib/queries';
import { Avatar, Favicon } from './Avatar';
import { cn } from './ui';

interface Suggestion extends Mailbox {
  contact: boolean;
  score: number;
}

/** Everyone the user knows: contacts first, then people seen in cached mail. */
export function useAddressBook(): Suggestion[] {
  const contacts = useContacts().data?.contacts ?? [];
  const qc = useQueryClient();
  return useMemo(() => {
    const map = new Map<string, Suggestion>();
    for (const c of contacts) for (const e of c.emails) map.set(e, { name: c.name, address: e, contact: true, score: 100 + (c.favorite ? 50 : 0) });
    for (const [, data] of qc.getQueriesData<InfiniteData<MessagePage>>({ queryKey: ['messages'] })) {
      for (const p of data?.pages ?? []) {
        for (const m of p.items) {
          for (const mb of [parseMailbox(m.from), ...parseAddressList(m.to), ...parseAddressList(m.cc)]) {
            if (!mb || /no-?reply|notifications?@|mailer-daemon/i.test(mb.address)) continue;
            const cur = map.get(mb.address);
            if (cur) cur.score += 1;
            else map.set(mb.address, { ...mb, contact: false, score: 1 });
          }
        }
      }
    }
    return [...map.values()].sort((a, b) => b.score - a.score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, qc.getQueryCache().getAll().length]);
}

export function RecipientInput({ label, value, onChange, autoFocus, exclude = [] }: { label: string; value: string[]; onChange: (v: string[]) => void; autoFocus?: boolean; exclude?: string[] }) {
  const [text, setText] = useState('');
  const [focus, setFocus] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const book = useAddressBook();
  const chips = value.map((v) => parseMailbox(v) ?? { address: v });
  const taken = new Set([...chips.map((c) => c.address), ...exclude.map((e) => e.toLowerCase())]);

  const suggestions = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) return [];
    return book.filter((s) => !taken.has(s.address) && (s.address.includes(q) || (s.name ?? '').toLowerCase().includes(q))).slice(0, 7);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, book, value]);

  const commit = (raw: string) => {
    const parsed = parseAddressList(raw).filter((m) => !taken.has(m.address));
    if (parsed.length) onChange([...value, ...parsed.map(formatMailbox)]);
    setText('');
    setActive(0);
  };

  return (
    <div className="relative flex min-h-[40px] items-start gap-2 border-b border-line px-4 py-1.5" onClick={() => input.current?.focus()}>
      <span className="w-10 shrink-0 pt-[7px] text-[12.5px] text-faint">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {chips.map((c, i) => {
          const ok = isValidEmail(c.address);
          return (
            <span key={c.address + i} className={cn('group inline-flex h-7 items-center gap-1.5 rounded-full border pl-1 pr-1.5 text-[12.5px]', ok ? 'border-line-strong bg-raised' : 'border-danger/50 bg-danger/10 text-danger')} title={c.address}>
              <Favicon domain={domainOf(c.address)} size={18} className="rounded-full" />
              <span className="max-w-[180px] truncate">{c.name ?? c.address}</span>
              <button
                className="rounded-full p-0.5 text-faint hover:bg-line hover:text-fg"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter((_, j) => j !== i));
                }}
              >
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        <input
          ref={input}
          autoFocus={autoFocus}
          value={text}
          onFocus={() => setFocus(true)}
          onBlur={() => {
            setTimeout(() => setFocus(false), 150);
            if (text.trim()) commit(text);
          }}
          onChange={(e) => {
            const v = e.target.value;
            if (/[,;\n]$/.test(v)) commit(v.slice(0, -1));
            else setText(v);
          }}
          onPaste={(e) => {
            const t = e.clipboardData.getData('text');
            if (/[,;\n]/.test(t) || t.includes('@')) {
              e.preventDefault();
              commit(text + t);
            }
          }}
          onKeyDown={(e) => {
            if (suggestions.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : -1) + suggestions.length) % suggestions.length);
            } else if ((e.key === 'Enter' || e.key === 'Tab') && (suggestions[active] || text.trim())) {
              if (e.key === 'Enter' || text.trim()) e.preventDefault();
              const s = suggestions[active];
              commit(s ? formatMailbox(s) : text);
            } else if (e.key === 'Backspace' && !text && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          className="h-7 min-w-[140px] flex-1 bg-transparent text-[13.5px] outline-none"
        />
      </div>
      {focus && suggestions.length > 0 && (
        <div className="glass absolute left-14 top-full z-30 mt-1 w-[360px] overflow-hidden rounded-xl bg-raised/95 p-1 shadow-2xl">
          {suggestions.map((s, i) => (
            <button
              key={s.address}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(formatMailbox(s));
              }}
              onMouseEnter={() => setActive(i)}
              className={cn('flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left', i === active && 'bg-line')}
            >
              <Avatar name={s.name ?? s.address} address={s.address} size={28} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium">{s.name ?? displayName(s)}</span>
                <span className="block truncate text-[11.5px] text-muted">{s.address}</span>
              </span>
              {s.contact && <span className="text-[10.5px] font-semibold uppercase tracking-wider text-faint">Contact</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
